import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "../forms.module.css";
import { useFormik, FormikProvider } from "formik";
import ContactTabsSection from "../contactTabsSection/contactTabsSection";
import { createDefaultDeviceContact, normalizeRegistrationConfig } from "../deviceContactSection/deviceContactSection";
import { addNewUserValidationSchema } from "../../../utils/validation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useAddUserMutation,
  useAdminResetUserPasswordMutation,
  useLazyGetUserByGuidQuery,
  useTherapistsSpecializations2TherapistsDMLMutation,
  useTherapistsSpecializationsDMLMutation,
} from "../../../services/api/user.api";
import Select, { ActionMeta, MultiValue, SingleValue } from "react-select";
import {
  IUserReport,
  TCompanyLabeledOption,
} from "../../../services/types/users.types";
import { useAppSelector } from "../../../services/hooks";
import {
  companiesSelector,
  rolesSelector,
  specializationsSelector,
} from "../../../services/selectors/users.selectors";
import { adminSelector } from "../../../services/selectors/user.selectors";
import Modal from "../../modals/modal/Modal";
import ErrorPopup from "../../modals/errorPopup/errorPopup";
import { appointmentsProgramsDataSelector } from "../../../services/selectors/utils.selectors";
import { useGetProtocolsByCompanyQuery, useGetTreatmentGroupsQuery, useGetCompanyByIdQuery, useGetAllCitiesQuery, useGetPaymentCollectionMethodsMutation } from "../../../services/api/utils.api";
import formStyles from "../forms.module.css";
import {
  addStaffUserInitialState,
  addUserInitialState,
  convertKeysToLowerCase,
  specializationAreas,
  getStatusOptions,
} from "../../../utils/constants";
import {
  AddLabelAndValue,
  TNamedItem,
} from "../../../services/types/utils.types";
import {
  TAddTherapistRequest,
  TAddUserRequest,
} from "../../../services/types/user.types";
import i18n from "../../../i18n";
import GeneralSuccessPopup from "../../modals/generalSuccessPopup/generalSuccessPopup";
import ChangeAvatarPopup from "../../modals/changeAvatarPopup/changeAvatarPopup";
import { AppImage } from "../../appMedia";
import CloseButton from "../../closeButton/closeButton";
import avatarPlaceholder from "../../../images/avatar_placeholder_purple.svg";
import ChangePasswordPopup from "../../modals/changePasswordPopup/changePasswordPopup";
import { TSpecialization } from "../../../types/types";
import UserPaymentTable from "../../userPaymentTable/userPaymentTable";
import AssignmentTable from "../../assignmentTable/assignmentTable";
import { isChargingCustomersSelector } from "../../../services/selectors/auth.selectors";
import UpdateUserBalanceComponent from "../updateUserBalanceComponent/updateUserBalanceComponent";

interface ContactPerson {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  relation: string;
  fakeCompanyId: number | null;
  passport: string;
  genderId: number | null;
  roleId: number;
  statusId: number;
  [key: string]: any;
}

const staffSpecializationsKey = (options: Partial<TSpecialization>[]): string =>
  [...options.map((o) => o.therapistsSpecializationsTypesId ?? 0)]
    .sort((a, b) => a - b)
    .join(",");

function AddNewUserForm({
  isStaff,
  onClose,
  user,
  onUserUpdated,
  activeFilters,
  initialTab,
  type,
}: {
  isStaff: boolean;
  onClose: (guid?: string) => void;
  user: IUserReport | undefined;
  onUserUpdated?: () => void;
  activeFilters?: any;
  initialTab?: 'payments' | 'therapists';
  type?: "createUser" | "editUser";
}) {
  const admin = useAppSelector(adminSelector);
  const showPaymentFeatures = useAppSelector(isChargingCustomersSelector);
  const companies = useAppSelector(companiesSelector);
  const roles = useAppSelector(rolesSelector);
  const appointmentsProgramsData = useAppSelector(
    appointmentsProgramsDataSelector
  );
  const specializations = useAppSelector(specializationsSelector);

  const [companiesOptions, setCompaniesOptions] = useState<
    TCompanyLabeledOption[]
  >([]);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'payments' | 'therapists'>(initialTab ?? 'therapists');
  const [paymentRefetchKey, setPaymentRefetchKey] = useState(0);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successFromPasswordOnly, setSuccessFromPasswordOnly] = useState(false);
  const [openAvatarModal, setOpenAvatarModal] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [infoText, setInfoText] = useState("");
  const [openErrorModal, setOpenErrorModal] = useState<string | undefined>(undefined);
  const contactsEndRef = useRef<HTMLDivElement>(null);
  /** Baseline for staff multi-select specializations (not tracked by Formik `dirty`). */
  const initialStaffSpecsKeyRef = useRef<string | null>(null);
  const [addUser] = useAddUserMutation();
  const [adminResetUserPassword] = useAdminResetUserPasswordMutation();
  const [selectedOptions, setSelectedOptions] = useState<
    Partial<TSpecialization>[]
  >([]);
  const [getAllSpecializations] = useTherapistsSpecializationsDMLMutation();
  const [therapistsSpecializations2Therapists] =
    useTherapistsSpecializations2TherapistsDMLMutation();
  const [getUserByGuid] = useLazyGetUserByGuidQuery();

  // const [updateUser] = useUpdateUserMutation();
  const { data: protocolsData } = useGetProtocolsByCompanyQuery(
    { companyId: user?.Users?.CompanyId || admin.companyId },
    { skip: !(user?.Users?.CompanyId || admin.companyId) }
  );
  const { data: treatmentGroupsData } = useGetTreatmentGroupsQuery(
    { companyId: user?.Users?.CompanyId || admin.companyId },
    { skip: !(user?.Users?.CompanyId || admin.companyId) || isStaff }
  );
  const { data: currentCompany } = useGetCompanyByIdQuery(admin.companyId, {
    skip: !admin.companyId,
  });
  const { data: citiesData } = useGetAllCitiesQuery();
  const [getPaymentCollectionMethods, { data: paymentCollectionMethodsData }] = useGetPaymentCollectionMethodsMutation();
  const statusOptions = getStatusOptions();

  const groupedOptions = useMemo(
    () =>
      specializationAreas.map((area) => ({
        label: t(area.labelKey),
        options: specializations
          .filter((spec) => spec.therapistsSpecializationsTypesId === area.value)
          .map((spec) => ({
            value: spec.id,
            label: spec.name,
          })),
      })),
    [specializations, i18n.language]
  );

  useEffect(() => {
    if (!user?.Users.Guid) return;

    getUserByGuid(user.Users.Guid)
      .unwrap()
      .then((personalData) => {
        formik.resetForm({
          values: {
            ...formik.values,
            ...personalData,
            protocolId: personalData.questionnaireCompaniesHeadersId ?? null,
          },
        });
      });
  }, [user?.Users.Guid]);
  useEffect(() => {
    if (admin.companyId && specializations.length === 0) {
      getAllSpecializations({
        list: [],
        type: 1,
        companyId: admin.companyId,
      });
    }
  }, [admin.companyId, specializations.length]);

  useEffect(() => {
    // Fetch payment collection methods
    getPaymentCollectionMethods();
  }, [getPaymentCollectionMethods]);

  // Ensure activeTab is 'therapists' when payments tab is not available
  useEffect(() => {
    if (!currentCompany?.isChargingCustomers && activeTab === 'payments') {
      setActiveTab('therapists');
    }
  }, [currentCompany?.isChargingCustomers, activeTab]);


  useEffect(() => {
    const options = companies.filter((c) => c.Id > 1);
    setCompaniesOptions([
      ...options,
      {
        Id: -2,
        value: -2,
        label: t('NO_SUBSIDIARY'),
        Name: t('NO_SUBSIDIARY'),
        NameEn: null,
        Guid: "",
      },
    ]);
  }, [companies]);

  useEffect(() => {
    if (!isStaff) {
      initialStaffSpecsKeyRef.current = null;
      return;
    }
    if (!user?.Users.Guid) {
      initialStaffSpecsKeyRef.current = staffSpecializationsKey([]);
    }
  }, [isStaff, user?.Users.Guid]);

  const form = isStaff ? addStaffUserInitialState : addUserInitialState;
  const formik = useFormik<TAddTherapistRequest | TAddUserRequest>({
    initialValues: {
      ...form,
      roleId: isStaff ? 2 : 1,
      fakeCompanyId: admin.fakeCompanyId,
      contacts: (user?.Contacts ?? []).map((c) => {
        const lower = { ...convertKeysToLowerCase(c ?? {}) } as ContactPerson;
        if (lower.deviceConfig || lower.relation === "device" || lower.roleId === 7) {
          lower.deviceConfig = normalizeRegistrationConfig(lower.deviceConfig);
          lower.relation = lower.relation || "device";
        }
        return lower;
      }), ...(user?.Users ? {
        ...convertKeysToLowerCase(user.Users),

        // Map QuestionnaireCompaniesHeadersId to protocolId for display
        protocolId: user.Users.QuestionnaireCompaniesHeadersId ?? user.Users.ProtocolId ?? null,
      } : {}),
      companyId: admin.companyId,
    },
    validationSchema: addNewUserValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values, { setSubmitting }) => {
      const { password, roles, id, ...rest } = values;
      // Strip fields managed by dedicated flows.
      const { avatar: _createAvatar, ...createBody } = values as any;
      const { balance: _balance, avatar: _avatar, ...restWithoutManagedFields } = rest as any;
      const roleChanged =
        Boolean(values.guid) &&
        user?.Users?.RoleId !== undefined &&
        values.roleId !== user.Users.RoleId;
      const body = !values.guid
        ? createBody
        : { ...restWithoutManagedFields, changeRole: roleChanged };
      // Handle form submission
      addUser({
        companyId: admin.companyId,
        body,
      })
        .then((res) => {
          if ("data" in res && res?.data) {
            const data = res.data;
            const saveSucceeded =
              ("updatedUserGuid" in data &&
                data.updatedUserGuid != null &&
                String(data.updatedUserGuid).length > 0) ||
              (!!values.guid && !("desc" in data));

            if (saveSucceeded) {
              const hadGuidBeforeSave =
                values.guid != null && String(values.guid).length > 0;

              let postSaveValues = { ...values };
              if (
                type === "createUser" &&
                data.updatedUserGuid != null &&
                String(data.updatedUserGuid).length > 0
              ) {
                const userGuid = data.updatedUserGuid;
                postSaveValues = {
                  ...values,
                  guid: userGuid,
                  id: data.updatedUserId ?? values.id,
                };
              }

              if (type === "createUser") {
                setSubmitting(false);
              } else {
                onUserUpdated?.();
              }

              const guidForSpecializations =
                postSaveValues.guid ?? data.updatedUserGuid ?? "";
              const shouldSyncSpecializations =
                String(guidForSpecializations).length > 0 &&
                (selectedOptions.length > 0 || hadGuidBeforeSave);

              formik.resetForm({ values: postSaveValues });
              if (isStaff) {
                initialStaffSpecsKeyRef.current =
                  staffSpecializationsKey(selectedOptions);
                setSelectedOptions((prev) => [...prev]);
              }

              if (shouldSyncSpecializations) {
                addTherapistsSpecializations2TherapistsList(
                  String(guidForSpecializations)
                );
              }

              setSuccessFromPasswordOnly(false);
              setOpenSuccessModal(true);
            }
            if ("isExistInAnotherCompany" in res.data
              && res.data.isExistInAnotherCompany !== null
              && res.data.isExistInAnotherCompany !== undefined
            ) {
              setInfoText(res.data?.isExistInAnotherCompany ?? "");
            }
            if ("desc" in res.data) {
              setOpenErrorModal(res.data.desc);
            }
          } else if ("error" in res) {
            setOpenErrorModal(t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER'));
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });


  const getLabelByValue = useCallback((value: number) => {
    for (const group of groupedOptions ?? []) {
      const found = group.options.find(opt => opt.value === value);
      if (found) return found.label;
    }
    return "";
  }, [groupedOptions]);

  useEffect(() => {
    // Wait for specialization options to be loaded so labels can be resolved
    if (!user?.Users.Guid || specializations.length === 0 || !isStaff) {
      return;
    }
    therapistsSpecializations2Therapists({
      list: [],
      type: 1,
      usersGuid: user.Users.Guid,
    }).then((res) => {
      if ("data" in res) {
        const loaded = res.data
          .filter((spec) => spec.statusId !== 0)
          .map((spec) => ({
            therapistsSpecializationsTypesId: spec.therapistsSpecializationsId,
            name: getLabelByValue(spec.therapistsSpecializationsId || 0),
          }));
        initialStaffSpecsKeyRef.current = staffSpecializationsKey(loaded);
        setSelectedOptions(loaded);
      }
    });
  }, [
    specializations,
    therapistsSpecializations2Therapists,
    user?.Users.Guid,
    isStaff,
    getLabelByValue,
  ]);

  const specializationsDirty =
    isStaff &&
    initialStaffSpecsKeyRef.current !== null &&
    staffSpecializationsKey(selectedOptions) !== initialStaffSpecsKeyRef.current;

  const hasUnsavedChanges = formik.dirty || specializationsDirty;

  const onSelectChange = (
    value: SingleValue<AddLabelAndValue<TNamedItem>>,
    { name }: ActionMeta<AddLabelAndValue<TNamedItem>>
  ) => {
    if (value && name) {
      formik.setFieldValue(name, value.Id);
    }
  };

  const handleSelectChange = (
    selected: MultiValue<{ value: number; label: string }> // עדכון הסוג
  ) => {
    setSelectedOptions(
      selected.map((option) => ({
        therapistsSpecializationsTypesId: option.value,
        name: option.label,
      }))
    );
  };

  const updatePassword = async (password: string) => {
    if (formik.values.guid) {
      try {
        await adminResetUserPassword({ targetUserGuid: formik.values.guid, newPassword: password }).unwrap();
        setSuccessFromPasswordOnly(true);
        setOpenSuccessModal(true);
      } catch {
        setOpenErrorModal(t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER'));
      }
    } else {
      formik.setFieldValue("password", password);
    }
  };

  const addTherapistsSpecializations2TherapistsList = (guid: string) => {
    if (isStaff) {
      therapistsSpecializations2Therapists({
        list: selectedOptions.map((selected) => ({
          id: 0,
          therapistsSpecializationsId: selected.therapistsSpecializationsTypesId || 0,
          usersId: 0,
          statusId: 1,
        })),
        type: 2,
        usersGuid: guid,
      }).then((res) => {
        if ("data" in res && res.data) {
          if ("desc" in res.data) {
            setOpenErrorModal(t('CHECK_TEST'));
          } else {
            initialStaffSpecsKeyRef.current =
              staffSpecializationsKey(selectedOptions);
            setSelectedOptions((prev) => [...prev]);
            setSuccessFromPasswordOnly(false);
            setOpenSuccessModal(true);
          }
        } else if ("error" in res) {
          setOpenErrorModal(t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER'));
        }
      });
    }
  };


  // this func should add or update the contact in the formik
  // case of the index is not exist - add it to the contact arr.
  const contactHandleChange = (index: number, field: string, value: any) => {
    const updatedContacts = [...formik.values.contacts ?? []];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value
    };
    formik.setFieldValue('contacts', updatedContacts);
  };

  // Set default payment collection method to first option for new users
  useEffect(() => {
    if (!isStaff && !user && paymentCollectionMethodsData && paymentCollectionMethodsData.length > 0 && !formik.values.paymentCollectionMethodId) {
      formik.setFieldValue("paymentCollectionMethodId", paymentCollectionMethodsData[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentCollectionMethodsData, user, isStaff]);



  return (
    <div className={styles.formWrapper}>
      <form
        className={`${styles.form} ${styles.form_type_addUser}`}
        data-testid="add-or-edit-user-form"
        onSubmit={formik.handleSubmit}
      >
        <CloseButton onClick={onClose} />
        <h2 className={`title title_blue`}>
          {user ? t('EDIT_USER_WITH_ID', { id: user.Users.Id }) : t('ADD_USER')} {user?.Users.RoleId == -2 && `- ${user.RoleName}`}
        </h2>

        {/* Profile image section - same as therapist/client profile */}
        {formik.values.guid && (
          <div className={formStyles.form__avatarSection}>
            <AppImage
              src={formik.values.avatar ? (formik.values.avatar as string) : avatarPlaceholder}
              alt={t('EDIT_PERSONAL_DATA_USER_AVATAR_ALT')}
              className={formStyles.form__avatarPlaceholder}
              onClick={() => setIsAvatarModalOpen(true)}
              data-testid="user-avatar"
            />
            <button
              type="button"
              className={formStyles.form__addAvatar}
              onClick={() => setOpenAvatarModal(true)}
              title={t('EDIT_PROFILE_PICTURE')}
              aria-label={t('EDIT_PROFILE_PICTURE')}
            />
            {isAvatarModalOpen && (
              <Modal onClose={() => setIsAvatarModalOpen(false)} wide={true}>
                <AppImage
                  className={formStyles.form__avatarModalContent}
                  src={formik.values.avatar ? (formik.values.avatar as string) : avatarPlaceholder}
                  alt="Profile Full"
                />
              </Modal>
            )}
          </div>
        )}

        <ul
          className={`${styles.form__list} ${styles.form__list_type_fourColumns} `}
        >
          <li>
            <label htmlFor="firstName" className={styles.form__label}>{t('FIRST_NAME')}</label>
            <input
              type="text"
              className={`${styles.form__input} ${formik.errors.firstName && styles.form__input_invalid
                }`}
              name="firstName"
              data-testid="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t('FIRST_NAME')}
            />
          </li>
          <li>
            <label htmlFor="lastName" className={styles.form__label}>{t('LAST_NAME')}</label>
            <input
              type="text"
              className={`${styles.form__input} ${formik.errors.lastName && styles.form__input_invalid
                }`}
              name="lastName"
              id="lastName"
              data-testid="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t('LAST_NAME')}
            />
          </li>
          <li>
            <label htmlFor="email" className={styles.form__label}>{t('EMAIL')}</label>
            <input
              type="string"
              className={`${styles.form__input} ${formik.errors.email && styles.form__input_invalid
                }`}
              name="email"
              id="email"
              data-testid="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t('EMAIL')}
            />
          </li>
          <li>
            <label htmlFor="passport" className={styles.form__label}>{t('ID_NUMBER')}</label>
            <input
              type="string"
              className={`${styles.form__input} ${formik.errors.passport && styles.form__input_invalid
                }`}
              name="passport"
              id="passport"
              data-testid="passport"
              value={formik.values.passport}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t('ID_NUMBER')}
            />
          </li>
          <li>
            <label htmlFor="birth" className={styles.form__label}>{t('DATE_OF_BIRTH')}</label>
            <input
              type="date"
              className={`${styles.form__input} ${formik.errors.birth && styles.form__input_invalid
                }`}
              name="birth"
              id="birth"
              data-testid="birthDate"
              value={formik.values.birth ? formik.values.birth.split("T")[0] : ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t('DATE_OF_BIRTH')}
              autoFocus={true}
            />
          </li>
          <li>
            <label htmlFor="phone" className={styles.form__label}>{t('MOBILE_PHONE')}</label>
            <input
              type="text"
              className={`${styles.form__input} ${styles.form__input_type_phone} ${formik.errors.phone && styles.form__input_invalid
                }`}
              name="phone"
              data-testid="phoneNumber"
              value={
                formik.values.phone
                  ? formik.values.phone.replace(/[^\d+]/g, "")
                  : ""
              }
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t('MOBILE_PHONE')}
            />
          </li>
          <li>
            <label htmlFor="cityId" className={styles.form__label}>{t('CITY')}</label>
            <Select
              name="cityId"
              inputId="cityId"
              options={citiesData?.map((city: { id: number; name: string }) => ({
                value: city.id,
                label: city.name,
              })) || []}
              value={citiesData?.find((city: { id: number; name: string }) => city.id === formik.values.cityId) ? {
                value: formik.values.cityId,
                label: citiesData.find((city: { id: number; name: string }) => city.id === formik.values.cityId)?.name || '',
              } : null}
              placeholder={`${t('CHOOSE')} ${t('CITY')}`}
              className={styles.select}
              isClearable
              onChange={(value) => {
                formik.setFieldValue("cityId", value?.value || null);
              }}
            />
          </li>
          <li>
            <label htmlFor="address" className={styles.form__label}>{t('ADDRESS')}</label>
            <input
              type="text"
              className={styles.form__input}
              name="address"
              id="address"
              data-testid="address"
              value={formik.values.address || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={t('ADDRESS')}
            />
          </li>
          <li>
            <label htmlFor="genderId" className={styles.form__label}>{t('GENDER')}</label>
            <Select
              name={"genderId"}
              inputId="genderId"
              options={appointmentsProgramsData.Genders}
              value={
                appointmentsProgramsData.Genders.find(
                  (o) => o.value === formik.values?.genderId
                ) || undefined
              }
              placeholder={t('SELECT_PLACEHOLDER')}
              className={styles.select}
              onChange={onSelectChange}
            />
          </li>
          <li>
            <label htmlFor="fakeCompanyId" className={styles.form__label}>{t('SUBSIDIARY')}</label>
            <Select
              name={"fakeCompanyId"}
              inputId="companyId"
              options={companiesOptions}
              value={
                companiesOptions.find(
                  (c) => c.value === formik.values.fakeCompanyId
                ) || undefined
              }
              placeholder={t('PLEASE_SELECT_SUBSIDIARY')}
              className={styles.select}
              onChange={(value) => {
                if (value) {
                  formik.setFieldValue(
                    "fakeCompanyId",
                    value.Id === -2 ? null : value.Id
                  );
                }
              }}
            />
          </li>
          <li>
            <label htmlFor="statusId" className={styles.form__label}>{t('COLUMN_STATUS')}</label>
            <Select
              name="statusId"
              inputId="statusId"
              options={statusOptions.map(opt => ({
                ...opt,
                label: t(opt.label)
              }))}
              value={statusOptions
                .map(opt => ({
                  ...opt,
                  label: t(opt.label)
                }))
                .find(opt => opt.value === formik.values.statusId?.toString())}
              placeholder={t("STATUS_PLACEHOLDER")}
              className={styles.select}
              onChange={(value) => {
                value && formik.setFieldValue("statusId", value.value);
              }}
            />
          </li>
          <li>
            <label htmlFor="protocolId" className={styles.form__label}>{t('PROTOCOL_FOR_PATIENT')}</label>
            <Select
              name="protocolId"
              inputId="protocolId"
              options={protocolsData?.map(protocol => ({
                value: protocol.id,
                label: protocol.desc,
              })) || []}
              value={protocolsData?.find(protocol => protocol.id === formik.values.protocolId) ? {
                value: formik.values.protocolId,
                label: protocolsData.find(protocol => protocol.id === formik.values.protocolId)?.desc || '',
              } : null}
              placeholder={t('SELECT_PROTOCOL')}
              className={styles.select}
              isClearable
              onChange={(value) => {
                formik.setFieldValue("protocolId", value?.value || null);
              }}
            />
          </li>
          {(!isStaff && user?.Users.RoleId != -2) && (
            <li>
              <label htmlFor="treatmentGroupId" className={styles.form__label}>{t('ASSIGN_TO_GROUP')}</label>
              <Select<{ value: number; label: string }>
                name="treatmentGroupId"
                inputId="treatmentGroupId"
                options={treatmentGroupsData?.filter(g =>
                  g.statusId === 1 && (!g.isFull || g.id === formik.values.treatmentGroupId)
                ).map(group => ({
                  value: group.id,
                  label: `${group.groupName}${group.maxParticipants ? ` (${group.currentMemberCount || 0}/${group.maxParticipants})` : ' (אין הגבלה)'}`,
                })) || []}
                value={
                  formik.values.treatmentGroupId
                    ? treatmentGroupsData
                      ?.map(group => ({
                        value: group.id,
                        label: `${group.groupName}${group.maxParticipants ? ` (${group.currentMemberCount || 0}/${group.maxParticipants})` : ' (אין הגבלה)'}`,
                      }))
                      .find(opt => opt.value === formik.values.treatmentGroupId) || null
                    : null
                }
                placeholder={t('SELECT_TREATMENT_GROUP_OPTIONAL')}
                className={styles.select}
                isClearable
                onChange={(value: SingleValue<{ value: number; label: string }>) => {
                  formik.setFieldValue("treatmentGroupId", value?.value || null);
                }}
              />
            </li>
          )}

          {isStaff && (
            <>
              <li>
                <label htmlFor="migzarId" className={styles.form__label}>{t('SECTOR')}</label>
                <Select
                  name={"migzarId"}
                  inputId="migzarId"
                  options={appointmentsProgramsData.Migzars}
                  value={
                    appointmentsProgramsData.Migzars.find(
                      (o) =>
                        o.value ===
                        (formik.values as TAddTherapistRequest).migzarId
                    ) || undefined
                  }
                  placeholder={t('SELECT_PLACEHOLDER')}
                  className={styles.select}
                  onChange={onSelectChange}
                />
              </li>
              <li>
                <label htmlFor="roleId" className={styles.form__label}>{t('ROLE')}</label>
                <Select
                  name={"roleId"}
                  inputId="roleId"
                  options={roles.filter((r) => (r.value === -3 || (r.value >= 0 && r.value !== 1)) && r.value !== 7)}
                  value={
                    roles.find((r) => r.value === formik.values.roleId) ||
                    undefined
                  }
                  placeholder={t('SELECT_PLACEHOLDER')}
                  className={styles.select}
                  onChange={(value) => {
                    value && formik.setFieldValue("roleId", value.value);
                  }}
                />
              </li>

              <li>
                <label htmlFor="specializationsId" className={styles.form__label}>
                  {t('FIELD_OF_SPECIALIZATION')}</label>
                <Select
                  isMulti={true}
                  name="specializations"
                  placeholder={t('CLICK_TO_SELECT')}
                  options={groupedOptions}
                  classNamePrefix="select"
                  onChange={handleSelectChange}
                  value={selectedOptions.map((option) => ({
                    value: option.therapistsSpecializationsTypesId || 0,
                    label: option.name || "",
                  }))}
                  isOptionDisabled={(option) =>
                    selectedOptions.some(
                      (selected) => selected.name === option.label
                    )
                  }
                />
              </li>
              <li>
                <label htmlFor="vetek" className={styles.form__label}>{t('TENURE')}</label>
                <input
                  type="number"
                  className={`${formStyles.form__input}`}
                  name="vetek"
                  id="vetek"
                  data-testid="vetek"
                  value={(formik.values as TAddTherapistRequest).vetek || ""}
                  onChange={formik.handleChange}
                />
              </li>

            </>
          )}
          {!isStaff && (
            <li>
              <label htmlFor="paymentCollectionMethodId" className={styles.form__label} style={{ fontWeight: 600 }}>{t('PAYMENT_COLLECTION_METHOD')}</label>
              <Select
                name="paymentCollectionMethodId"
                inputId="paymentCollectionMethodId"
                options={paymentCollectionMethodsData?.map((method) => ({
                  value: method.id,
                  label: method.typeName || '',
                })) || []}
                value={paymentCollectionMethodsData?.find((method) => method.id === formik.values.paymentCollectionMethodId) ? {
                  value: formik.values.paymentCollectionMethodId,
                  label: paymentCollectionMethodsData.find((method) => method.id === formik.values.paymentCollectionMethodId)?.typeName || '',
                } : null}
                placeholder={t('SELECT_PAYMENT_COLLECTION_METHOD')}
                className={styles.select}
                isClearable
                onChange={(value) => {
                  formik.setFieldValue("paymentCollectionMethodId", value?.value || null);
                }}
              />
            </li>
          )}
          <li>
            <span
              className={`${styles.form__label} underlined pointer`}
              onClick={() => setOpenPasswordModal(true)}
            >{t('EDIT_PASSWORD')}</span>
            <input
              type="password"
              className={`${styles.form__input} ${formik.errors.password && styles.form__input_invalid
                }`}
              name="password"
              id="password"
              data-testid="password"
              value={formik.values.password || ""}
              onBlur={formik.handleBlur}
              readOnly
              onClick={() => setOpenPasswordModal(true)}
            />
          </li>
          {(isStaff && showPaymentFeatures && formik.values.roleId > 1) ?
            (<li>
              <label htmlFor="balance" className={styles.form__label}>{t('THERAPIST_SESSIONS_PRICE')}</label>
              <input
                type="number"
                className={`${styles.form__input}`}
                name="sessionCost"
                id="sessionCost"
                value={(formik.values as TAddTherapistRequest).sessionCost ?? null}
                onChange={(e) => {
                  const val = e.target.value;
                  formik.setFieldValue(
                    "sessionCost",
                    val === "" ? null : Number(val)
                  );
                }} />
            </li>) : null}
        </ul>

        {/* Contact Persons / Device contacts. Devices belong only to technicians (roleId 8). */}
        {(!isStaff || user?.Users.RoleId == -2) ? (
          <FormikProvider value={formik}>
            <ContactTabsSection
              genders={appointmentsProgramsData.Genders.map(gender => ({
                value: typeof gender.value === 'string' ? parseInt(gender.value) : gender.value,
                label: gender.label
              }))}
              usersRoleId={user?.Users.RoleId}
              handleChange={contactHandleChange}
              companiesOptions={companiesOptions}
              onClose={onClose}
              contactMode="person"
              onAddContact={() => {
                const newContact: ContactPerson = {
                  firstName: '',
                  lastName: '',
                  phone: '',
                  email: '',
                  relation: '',
                  fakeCompanyId: null,
                  passport: '',
                  genderId: null,
                  statusId: 1,
                  roleId: user?.Users.RoleId == -2 ? 1 : -2
                };
                const newContacts = [...formik.values.contacts ?? [], newContact];
                formik.setFieldValue('contacts', newContacts);
              }}
            />
            <div ref={contactsEndRef} />
          </FormikProvider>
        ) : (isStaff && formik.values.roleId === 8) ? (
          <FormikProvider value={formik}>
            <ContactTabsSection
              genders={[]}
              usersRoleId={user?.Users.RoleId}
              handleChange={contactHandleChange}
              companiesOptions={companiesOptions}
              onClose={onClose}
              contactMode="device"
              onAddContact={() => {
                const newContacts = [...formik.values.contacts ?? [], createDefaultDeviceContact()];
                formik.setFieldValue('contacts', newContacts);
              }}
            />
            <div ref={contactsEndRef} />
          </FormikProvider>
        ) : null}
        {showPaymentFeatures && user?.Users.RoleId == 1 && <UpdateUserBalanceComponent user={user} onSuccess={() => { onUserUpdated?.(); setPaymentRefetchKey(k => k + 1); }} />}

        {formik.errors && (
          <p
            className={`error ${styles.signin_private__invalid_message}`}
            data-testid="params-error-by-add-user"
          >
            {t(
              formik.errors.firstName ??
              formik.errors.lastName ??
              formik.errors.email ??
              formik.errors.passport ??
              formik.errors.birth ??
              formik.errors.phone ??
              formik.errors.password
            )}
          </p>
        )}
        <div className={styles.form__submitRow}>
          {hasUnsavedChanges && (
            <span
              className={styles.form__unsavedHint}
              role="status"
              aria-live="polite"
            >
              {t("USER_FORM_UNSAVED_CHANGES")}
            </span>
          )}
          <button
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting}
            className={`button ${styles.form__btn} ${!formik.isValid || formik.isSubmitting
              ? styles.form__btn_disabled
              : "hover"
              }`}
          >{t('UPDATE_1')}</button>
        </div>
        {openSuccessModal && (
          <Modal
            onClose={() => {
              const wasPasswordOnly = successFromPasswordOnly;
              setOpenSuccessModal(false);
              setSuccessFromPasswordOnly(false);
              if (!wasPasswordOnly) onClose();
            }}
          >
            <GeneralSuccessPopup
              infoText={infoText}
              onClose={() => {
                const wasPasswordOnly = successFromPasswordOnly;
                setOpenSuccessModal(false);
                setSuccessFromPasswordOnly(false);
                if (!wasPasswordOnly && type !== "createUser") onClose();
              }}
            />
          </Modal>
        )}
        {openErrorModal && (
          <Modal
            onClose={() => {
              setOpenErrorModal(undefined);
            }}
          >
            <ErrorPopup
              text={openErrorModal}
              onClose={() => {
                setOpenErrorModal(undefined);
              }}
            />
          </Modal>
        )}
        {openPasswordModal && (
          <Modal onClose={() => setOpenPasswordModal(false)}>
            <ChangePasswordPopup
              onClose={() => setOpenPasswordModal(false)}
              updatePassword={updatePassword}
            />
          </Modal>
        )}
        {openAvatarModal && formik.values.id && formik.values.guid && (
          <Modal onClose={() => setOpenAvatarModal(false)} wide={true}>
            <ChangeAvatarPopup
              onClose={() => setOpenAvatarModal(false)}
              onSuccess={(newAvatarUrl) => {
                if (newAvatarUrl !== undefined) {
                  formik.setFieldValue("avatar", newAvatarUrl);
                }
                setOpenAvatarModal(false);
                onUserUpdated?.();
              }}
              userId={formik.values.id}
              userGuid={formik.values.guid}
              currentAvatar={formik.values.avatar || null}
            />
          </Modal>
        )}
      </form>

      {/* Client View: Tabs for Payment History and Therapists */}
      {formik.values.guid && !isStaff && (
        <>
          <div className={formStyles.contactTabsContainer} style={{ marginTop: '0', marginBottom: '20px', background: 'transparent', boxShadow: 'none', border: 'none', borderRadius: '0' }}>
            <div className={formStyles.contactTabsHeader} style={{ paddingLeft: '0', paddingRight: '0', borderBottom: 'none', background: 'transparent', borderRadius: '0' }}>
              <div className={formStyles.contactTabsList}>
                <button
                  type="button"
                  className={`${formStyles.contactTab} ${activeTab === 'therapists' ? formStyles.contactTab_active : ''}`}
                  style={{ borderRadius: '0', background: 'transparent', boxShadow: 'none' }}
                  onClick={() => setActiveTab('therapists')}
                >
                  {t("PATIENT_THERAPISTS")}
                </button>
                {currentCompany?.isChargingCustomers && (
                  <button
                    type="button"
                    className={`${formStyles.contactTab} ${activeTab === 'payments' ? formStyles.contactTab_active : ''}`}
                    style={{ borderRadius: '0', background: 'transparent', boxShadow: 'none' }}
                    onClick={() => setActiveTab('payments')}
                  >
                    {t("USER_PAYMENTS_HISTORY")}
                  </button>
                )}
              </div>
            </div>
          </div>

          {activeTab === 'payments' && currentCompany?.isChargingCustomers && formik.values.id && (
            <UserPaymentTable
              key={paymentRefetchKey}
              userId={formik.values.id}
              userGuid={formik.values.guid}
              hideTitle={true}
              clientPhone={formik.values.phone}
              clientEmail={formik.values.email}
            />
          )}

          {activeTab === 'therapists' && formik.values.guid && (
            <AssignmentTable mode="patient-therapists" userGuid={formik.values.guid} />
          )}
        </>
      )}

      {/* Staff View: Therapist's Patients */}
      {isStaff && formik.values.guid && (
        <>
          <div className={formStyles.contactTabsContainer} style={{ marginTop: '0', marginBottom: '20px', background: 'transparent', boxShadow: 'none', border: 'none', borderRadius: '0' }}>
            <div className={formStyles.contactTabsHeader} style={{ paddingLeft: '0', paddingRight: '0', borderBottom: 'none', background: 'transparent', borderRadius: '0' }}>
              <div className={formStyles.contactTabsList}>
                <button
                  type="button"
                  className={`${formStyles.contactTab} ${formStyles.contactTab_active}`}
                  style={{ cursor: 'default', borderRadius: '0', background: 'transparent', boxShadow: 'none' }} // Static header, non-clickable appearance
                >
                  {t("THERAPIST_PATIENTS")}
                </button>
              </div>
            </div>
          </div>
          <AssignmentTable mode="therapist-patients" userGuid={formik.values.guid} />
        </>
      )}
    </div>
  );
}

export default AddNewUserForm;
